import React from "react";
import toLower from "lodash/toLower";
import queryString from "query-string";

import FormContainer from "@ats/src/components/forms/FormContainer";
import SessionFormInput from "@ats/src/views/sessions/components/SessionFormInput";
import SessionButton from "@ats/src/views/sessions/components/SessionButton";

import { validateRegistration } from "@shared/lib/validateWithYup";
import { adPlatformIdentifiers, sanitizeTrackingParams } from "@shared/lib/utils";

import { useRegister } from "@shared/queryHooks/useSession";
import { useGetInvite } from "@shared/queryHooks/useInvite";

function SignupForm(props) {
  const [state, setState] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const inviteToken = queryString.parse(props.location.search).invite_token;
  const [referral, setReferral] = React.useState(queryString.parse(props.location.search).referral);
  const [trackingParams] = React.useState(sanitizeTrackingParams(props.location.search));
  const [adPlatformValues] = React.useState(adPlatformIdentifiers(props.location.search));
  const [errors, setErrors] = React.useState(null);

  const googleClickId = adPlatformValues.googleClickId
    ? adPlatformValues.googleClickId
    : adPlatformValues.googleFirstPartyCookie;
  const liFatId = adPlatformValues.liFatId
    ? adPlatformValues.liFatId
    : adPlatformValues.linkedinFirstPartyCookie;

  const { firstName, lastName, email, password } = state;
  const { mutate: register, isLoading } = useRegister();
  const { data: inviteData, isLoading: isLoadingInvite } = useGetInvite(inviteToken);

  const inviteEmail = inviteData?.email;
  React.useEffect(() => {
    if (inviteEmail != undefined) {
      setState((state) => ({ ...state, email: inviteEmail }));
      setReferral("invite");
    }
  }, [inviteEmail]);

  const handleSignup = async (e) => {
    e.preventDefault();
    window.logger("%c[SignupForm] handleSignup state", "color: #1976D2", state);

    setErrors(null);

    // Trim email and update state
    const trimmedEmail = email.trim();
    setState({ ...state, email: trimmedEmail });

    const [isValid, validationErrors] = await validateRegistration({
      firstName,
      lastName,
      email: trimmedEmail,
      password,
    });

    if (isValid) {
      register(
        {
          firstName,
          lastName,
          email: toLower(trimmedEmail),
          password,
          inviteToken,
          referral,
          utmSource: trackingParams.utmSource,
          utmCampaign: trackingParams.utmCampaign,
          utmData: trackingParams.utmData,
          internalRef: trackingParams.internalRef,
          adrollClickId: adPlatformValues.adrollClickId,
          gaClientId: adPlatformValues.gaClientId,
          gaSessionId: adPlatformValues.gaSessionId,
          fbclid: adPlatformValues.fbclid,
          fbp: adPlatformValues.fbp,
          fbc: adPlatformValues.fbc,
          liFatId: liFatId,
          googleClickId: googleClickId,
          adrollFirstPartyCookie: adPlatformValues.adrollFirstPartyCookie,
          posthogDistinctId: adPlatformValues.posthogDistinctId,
        },
        {
          onSuccess: (data) => {
            window.logger("[SignupForm] register onScucess", { data });
            props.onComplete();
          },
          onError: (response: any) => {
            window.logger("[SignupForm] server error", { response });
            if (response.data.errors) {
              setErrors(response.data.errors);
            }
          },
        },
      );
    } else {
      setErrors(validationErrors);
    }
  };

  const handleInputChange = (name, value) => {
    setErrors(null);
    setState({ ...state, [name]: value });
  };

  const button = (
    <SessionButton type="submit" disabled={isLoading} loading={isLoading}>
      Sign up
    </SessionButton>
  );

  return (
    <FormContainer onSubmit={handleSignup} errors={errors} buttons={button}>
      <SessionFormInput
        onChange={handleInputChange}
        name="firstName"
        placeholder=""
        label="First name"
        className=""
        errors={errors}
      />

      <SessionFormInput
        onChange={handleInputChange}
        name="lastName"
        placeholder=""
        label="Last name"
        className=""
        errors={errors}
      />

      <SessionFormInput
        onChange={handleInputChange}
        isDisabled={inviteToken != null}
        name="email"
        placeholder=""
        label="Email address"
        inputType="email"
        className=""
        value={email}
        errors={errors}
      />

      <SessionFormInput
        onChange={handleInputChange}
        name="password"
        placeholder=""
        label="Password"
        className=""
        inputType="password"
        errors={errors}
      />
    </FormContainer>
  );
}

export default SignupForm;
